import React, {
  useMemo, useState, useCallback, createContext, useContext,
} from 'react';

import {
  FightIcon, FireIcon, GunIcon, PeopleIcon, BalaklavaIcon, BriefcaseIcon, LyingPersonIcon, HandsUpIcon,
  FallIcon,
} from '@vlabs/icons';
import { openConfirmPopup } from '@vlabs/uikit';
import i18n from 'i18next';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  getAnalyticOrdinalNumbers,
} from '@vlabs/pages/video-analytics/getAnalyticOrdinalNumbers';
import { getDefaultVaSettingsByType } from '@vlabs/pages/video-analytics/utils';

import { SubForms } from './sub-forms';

export const AnalyticsArrayContext = createContext();

const ANALYTIC_ICONS = {
  people_count: <PeopleIcon />,
  fights_analytics: <FightIcon />,
  weapon_analytics: <GunIcon />,
  fire_analytics: <FireIcon />,
  facecover_analytics: <BalaklavaIcon />,
  bags_analytics: <BriefcaseIcon />,
  lying_analytics: <LyingPersonIcon />,
  handsup_analytics: <HandsUpIcon />,
  fall_analytics: <FallIcon />,
};

const ANALYTIC_LABELS = {
  get people_count() {
    return i18n.t('videoAnalytics:form.crowd.title');
  },
  get fights_analytics() {
    return i18n.t('videoAnalytics:form.fight.title');
  },
  get weapon_analytics() {
    return i18n.t('videoAnalytics:form.weapon.title');
  },
  get fire_analytics() {
    return i18n.t('videoAnalytics:form.fire.title');
  },
  get facecover_analytics() {
    return i18n.t('videoAnalytics:form.facecover.title');
  },
  get bags_analytics() {
    return i18n.t('videoAnalytics:form.bags.title');
  },
  get lying_analytics() {
    return i18n.t('videoAnalytics:form.lying.title');
  },
  get handsup_analytics() {
    return i18n.t('videoAnalytics:form.handsup.title');
  },
  get fall_analytics() {
    return i18n.t('videoAnalytics:form.fall.title');
  },
  get queue() {
    return i18n.t('videoAnalytics:form.queue.title');
  },
};

const ANALYTIC_FORMS = {
  people_count: SubForms.CrowdSubForm,
  fights_analytics: SubForms.FightSubForm,
  weapon_analytics: SubForms.WeaponSubForm,
  fire_analytics: SubForms.FireSubForm,
  facecover_analytics: SubForms.FacecoverSubForm,
  bags_analytics: SubForms.BagsSubForm,
  lying_analytics: SubForms.LyingSubForm,
  handsup_analytics: SubForms.HandsupSubForm,
  fall_analytics: SubForms.FallSubForm,
};

export function AnalyticsArrayProvider({ children, analyticForms = ANALYTIC_FORMS, analyticLabels = ANALYTIC_LABELS }) {
  const { t } = useTranslation();
  const { control, formState: { errors } } = useFormContext();

  const [activeSection, setActiveSection] = useState('common');

  const fieldArray = useFieldArray({
    control,
    name: 'analytics',
    keyName: 'key',
  });

  const addAnalytic = useCallback((type) => {
    fieldArray.append(getDefaultVaSettingsByType(type));
  }, []);

  const deleteAnalytic = useCallback((index, analyticName) => {
    const message = t('videoAnalytics:form.modal.delete.message', { analyticName });

    openConfirmPopup({
      title: t('videoAnalytics:form.modal.delete.title'),
      message,
      type: 'delete',
      onConfirm: () => fieldArray.remove(index),
    });
  }, [t]);

  const analyticFields = useMemo(() => {
    const analyticsOrdinalNumbers = getAnalyticOrdinalNumbers(fieldArray.fields);

    return fieldArray.fields.map((analytic, index) => {
      const labelText = analyticLabels?.[analytic.analytic_name] ?? analytic.analytic_name;
      const ordinal = analyticsOrdinalNumbers[index] ?? index + 1;

      const Form = analyticForms?.[analytic.analytic_name];

      return {
        ...analytic,
        Form,
        id: `${analytic.analytic_name}-${index}`,
        label: labelText,
        icon: ANALYTIC_ICONS?.[analytic.analytic_name],
        labelWithOrdinal: `${labelText} ${ordinal}`,
        hasError: Boolean(errors.analytics?.[index]),
        remove: () => deleteAnalytic(index, `${labelText} ${ordinal}`),
      };
    });
  }, [fieldArray.fields, errors]);

  const onChangeActiveSection = useCallback((section) => setActiveSection(section), []);

  const contextValue = useMemo(() => ({
    onChangeActiveSection,
    analyticFields,
    activeSection,
    deleteAnalytic,
    addAnalytic,
    // доступ к оригинальному массиву формы
    fieldArray,
  }), [
    onChangeActiveSection,
    analyticFields,
    addAnalytic,
    activeSection,
    deleteAnalytic,
    fieldArray,
  ]);

  return (
    <AnalyticsArrayContext.Provider value={contextValue}>
      {children}
    </AnalyticsArrayContext.Provider>
  );
}

export function useAnalyticsContext() {
  const context = useContext(AnalyticsArrayContext);
  if (!context) throw new Error('useAnalyticsContext: must be used within AnalyticsArrayProvider');

  return context;
}
